import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import ModalCloseSvg from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import MagnifyingGlassSvg from '@adsk/bim360-matrix-react-assets/magnifying-glass.svg';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';

import './_SearchBox.scss';

/**
 * A controlled search input component used for search
 */
export default class SearchBox extends React.Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.ActionActivatorKeys = ['Enter'];
  }

  /**
   * Renders the clear button
   */
  _renderClearButton() {
    // the onMouseDown is on order to prevent focus from propagating into parent elements
    return (
      <IconButton
        className={classNames('SearchBox__close-button', {
          'SearchBox__close-button--hidden': !this.props.searchExpression
        })}
        tabIndex={0}
        onKeyPress={e => this.ActionActivatorKeys.indexOf(e.key) > -1 && this.props.onClear()}
        onMouseDown={e => {
          e.stopPropagation();
          this.props.onClear();
        }}
        disabled={this.props.clearDisabled}
      >
        <SvgIcon width="12px" height="12px">
          {ModalCloseSvg}
        </SvgIcon>
      </IconButton>
    );
  }

  componentDidMount() {
    if (this.props.isFocused && this.input) {
      this.input.focus();
    }

    if (this.props.onInputRefMount) {
      this.props.onInputRefMount(this.input);
    }
  }

  render() {
    const classes = classNames('SearchBox', this.props.className, {
      'SearchBox--disable-focus-indication': this.props.disableInputFocusIndication
    });

    return (
      <div className={classes}>
        <IconButton
          className="SearchBox__search-button"
          tabIndex={0}
          onKeyPress={e => this.ActionActivatorKeys.indexOf(e.key) > -1 && this.props.onSearch()}
          onMouseDown={this.props.onSearch}
          disabled={this.props.searchDisabled}
        >
          <SvgIcon>{MagnifyingGlassSvg}</SvgIcon>
        </IconButton>
        <input
          type="search"
          {...this.props.inputProps}
          className="SearchBox__input"
          ref={ref => {
            this.input = ref;
          }}
          placeholder={this.props.placeholder}
          value={this.props.searchExpression}
          onChange={e => this.props.onChange(e.target.value)}
          onFocus={e => this.props.onFocus()}
          onBlur={e => this.props.onBlur()}
          onKeyPress={e => this.ActionActivatorKeys.indexOf(e.key) > -1 && this.props.onSearch()}
          disabled={this.props.inputDisabled}
        />
        {this._renderClearButton()}
      </div>
    );
  }
}

SearchBox.defaultProps = {
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  inputProps: {},
  disableInputFocusIndication: false
};

SearchBox.propTypes = {
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * The current search expression text
   */
  searchExpression: PropTypes.string.isRequired,
  /**
   * A callback function for on search event
   */
  onSearch: PropTypes.func.isRequired,
  /**
   * A callback function for on click event
   */
  onClear: PropTypes.func,
  /**
   * A callback function for on change event
   */
  onChange: PropTypes.func,
  /**
   * A callback function for on focus event
   */
  onFocus: PropTypes.func,
  /**
   * A callback function for on blur event
   */
  onBlur: PropTypes.func,
  /**
   * If true is passed, visual indication of the input focus will not be shown
   */
  disableInputFocusIndication: PropTypes.bool,
  /**
   * A placeholder text to be displayed when the search expression is empty
   */
  placeholder: PropTypes.string,
  /**
   * Whether the component should be focused
   */
  isFocused: PropTypes.bool,
  /**
   * Whether the input should be disabled
   */
  inputDisabled: PropTypes.bool,
  /**
   * Whether the search button should be disabled
   */
  searchDisabled: PropTypes.bool,
  /**
   * Whether the clear button should be disabled
   */
  clearDisabled: PropTypes.bool,
  /**
   * Function to call upon mounting of the SearchBox Input, returns the ref
   */
  onInputRefMount: PropTypes.func,
  /**
   * Additional input props
   */
  inputProps: PropTypes.object
};
